import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDispatch } from 'react-redux';

import { trackProductListViewed } from '../actions/products';
import { removeTrackingList, updateTrackingList } from '../actions/tracking';
import { gatherProductListTrackingData } from '../segment-events/utils';

type EntryData = {
  entryObj: IntersectionObserverEntry;
  isVisible: boolean;
};
export function useProductTrackingUtils({
  products,
  tracking,
  listId,
  fplId,
  profile,
  time,
}) {
  const [carouselId] = useState(listId || fplId);
  const productMap = useRef({});
  const seenProductMap = useRef(tracking || {});
  const isVisible = useRef(false);
  const observer = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    productMap.current = products.reduce(
      (
        acc: object,
        { masterProductId: mpId, master_product_id: _masterProductId, ...rest },
        index: number
      ) => {
        const masterProductId = _masterProductId || mpId;

        if (masterProductId) {
          acc[masterProductId] = {
            masterProductId,
            ...rest,
            position: index + 1,
          };
        }
        return acc;
      },
      {}
    );
  }, [products]);

  const setObserver = useCallback(obsrvr => {
    if (observer.current === null) {
      observer.current = obsrvr;
    }
  }, []);

  const clearTracking = useCallback(() => {
    dispatch(removeTrackingList(carouselId));
    isVisible.current = false;
    seenProductMap.current = {};
  }, [carouselId, dispatch]);

  const batchCallBack = useCallback(
    (callback, dbTime = 1000) => {
      let timeout: ReturnType<typeof setTimeout>;
      let isExecuting = false;
      const batchedEntries: EntryData[] = [];

      return (entries: IntersectionObserverEntry[]) => {
        batchedEntries.push(
          ...entries.map(obj => {
            return { entryObj: obj, isVisible: isVisible.current };
          })
        );
        if (!isExecuting) {
          clearTimeout(timeout);
        }

        timeout = setTimeout(async () => {
          isExecuting = true;
          await callback(batchedEntries);
          isExecuting = false;
        }, dbTime);
      };
    },
    [isVisible]
  );

  const addProductRef = useCallback(
    (id, ref) => {
      if (!productMap.current[id]) {
        return;
      }

      if (productMap.current[id].ref) {
        if (productMap.current[id].ref instanceof Element) {
          observer.current?.unobserve(productMap.current[id].ref);
        }
      }

      if (!ref) {
        productMap.current = {
          ...productMap.current,
          [id]: {
            ...productMap.current[id],
            ref: null,
          },
        };
        return;
      }

      productMap.current = {
        ...productMap.current,
        [id]: { ...productMap.current[id], ref: { ...ref } },
      };
      if (productMap.current[id].ref instanceof Element) {
        observer.current?.observe(productMap.current[id].ref);
      }
    },
    [productMap]
  );

  const refreshProducts = useCallback(() => {
    observer.current?.disconnect();

    Object.keys(productMap.current).forEach(key => {
      if (!seenProductMap.current[key]) {
        const element = document.getElementById(key);
        if (element) {
          observer.current.observe(element);
        }
      }
    });
  }, [productMap, seenProductMap]);

  const processBatchedEntries = useCallback(
    async (batchedEntries: EntryData[]) => {
      const productsToBeTracked = [];
      while (batchedEntries.length) {
        const entry = batchedEntries.pop();
        const {
          entryObj: { isIntersecting, target },
          isVisible,
        } = entry;

        if (isVisible) {
          const productId = target.id;

          if (isIntersecting && !seenProductMap.current[productId]) {
            productsToBeTracked.push(productMap.current[productId]);
            seenProductMap.current = {
              ...seenProductMap.current,
              [productId]: productMap.current[productId],
            };
          }
        }
      }

      if (productsToBeTracked.length) {
        const enhancedProducts = await gatherProductListTrackingData(
          productsToBeTracked,
          {
            dispatch,
            time,
            customerDetails: { profile },
          }
        );
        dispatch(updateTrackingList(carouselId, seenProductMap));
        dispatch(
          trackProductListViewed({
            fpl_id: fplId,
            list_id: listId,
            products: enhancedProducts,
          })
        );
      }
    },
    [
      carouselId,
      dispatch,
      fplId,
      listId,
      productMap,
      profile,
      seenProductMap,
      time,
    ]
  );

  const containerCallback = useCallback(
    entries => {
      isVisible.current =
        entries.length > 0 && entries[0].intersectionRatio >= 0.99;

      refreshProducts();
    },
    [refreshProducts]
  );

  return useMemo(
    () => ({
      setObserver,
      refreshProducts,
      clearTracking,
      addProductRef,
      productCallback: batchCallBack(processBatchedEntries, 1000),
      containerCallback,
    }),
    [
      addProductRef,
      batchCallBack,
      clearTracking,
      containerCallback,
      processBatchedEntries,
      refreshProducts,
      setObserver,
    ]
  );
}
